/**
 * GmLayout — Sidebar + TopNavbar + Main Content wrapper for the GM Dashboard.
 *
 * Mirrors MLBLayout's flex shell, but adds a glassmorphic top bar above the
 * <main> outlet (the basketball Coach Dashboard convention). Page-level
 * headers (icon + title + subtitle + animated gold underline) live inside
 * each route component, NOT in this layout.
 */

import React, { useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { ChevronDown, Menu } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';
import GmSidebar from './GmSidebar';
import TeamPicker from '../mlb/overview/TeamPicker';
import { useMlbTeam } from '../../lib/mlb/MlbTeamContext';
import { useMlbTeamData } from '../../lib/mlb/MlbTeamDataContext';

const TOP_BAR_HEIGHT = 56;
const SIDEBAR_OPEN_WIDTH = 'var(--sidebar-width, 240px)';
const SIDEBAR_RAIL_WIDTH = '64px';
const HEADER_HEIGHT = 'var(--header-height, 56px)';

/* -----------------------------------------------------------------------------
 * GmTopNavbar — internal subcomponent, kept in this file so the GM shell is
 * fully described by the two spec files. Glassmorphism via inline backdrop-
 * filter plus the `backdrop-blur-kinetic-glass` Tailwind utility for parity
 * with the rest of the kinetic system.
 * --------------------------------------------------------------------------- */
function GmTopNavbar({ sidebarCollapsed, isMobile, onToggleSidebar }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { team, setTeam } = useMlbTeam();
  const { abbr, loadingPhase, loadingProgress } = useMlbTeamData();

  // Hardcoded user — wire up to real auth in a follow-up.
  const user = { name: 'James Schlauch', role: 'ADMIN', initials: 'JS' };

  // Cascade loading indicator — shown to the LEFT of the team picker. Visible
  // for any active phase (roster · leaderboards · profiles); hidden once the
  // cascade is `done` or hasn't started yet (`idle`).
  const cascadeActive = loadingPhase !== 'idle' && loadingPhase !== 'done';
  const phaseLabel = (() => {
    if (loadingPhase === 'roster') return 'roster';
    if (loadingPhase === 'leaderboards') return 'leaderboards';
    if (loadingPhase === 'profiles') {
      const { profilesLoaded = 0, profilesTotal = 0 } = loadingProgress || {};
      return profilesTotal > 0
        ? `profiles ${profilesLoaded}/${profilesTotal}`
        : 'profiles';
    }
    return loadingPhase;
  })();

  return (
    <header
      className="backdrop-blur-kinetic-glass"
      style={{
        flex: '0 0 auto',
        height: `${TOP_BAR_HEIGHT}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        background: 'rgba(16, 20, 26, 0.7)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        boxSizing: 'border-box',
      }}
    >
      {/* Left — Hamburger (mobile) + client badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {isMobile && (
          <button
            type="button"
            onClick={onToggleSidebar}
            aria-label="Open menu"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              color: 'var(--text-secondary, #999077)',
              cursor: 'pointer',
            }}
          >
            <Menu size={16} />
          </button>
        )}
        <div
          style={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: '#FED603',
            fontFamily: 'var(--font-family-headline, Montserrat, sans-serif)',
          }}
        >
          SLAM SPORTS AI
        </div>
      </div>

      {/* Center — cascade loading indicator (left) + global team picker. The
          indicator is a 14px gold pulsing dot + label, visible only while a
          cascade phase is active. Width-constrained wrapper since TeamPicker
          only exposes value/onChange; the inner combobox fills the wrapper
          width. Hidden visually on very small screens via shrink — the input
          still renders, just narrower. */}
      <div
        style={{
          flex: '0 1 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          margin: '0 12px',
        }}
      >
        <style>{`
          @keyframes gmTeamCascadePulse {
            0%, 100% { opacity: 0.45; transform: scale(0.85); }
            50% { opacity: 1; transform: scale(1); }
          }
        `}</style>
        {cascadeActive && (
          <div
            aria-live="polite"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: 'var(--text-tertiary, #9ea6b4)',
              fontFamily: 'var(--font-family-headline, Montserrat, sans-serif)',
              letterSpacing: '0.04em',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: '#FED603',
                boxShadow: '0 0 10px rgba(254, 214, 3, 0.5)',
                animation: 'gmTeamCascadePulse 1.2s ease-in-out infinite',
                flexShrink: 0,
              }}
            />
            {!isMobile && (
              <span>
                Loading <span style={{ color: '#FED603', fontWeight: 700 }}>{abbr}</span>
                <span style={{ opacity: 0.6 }}> &middot; {phaseLabel}</span>
              </span>
            )}
          </div>
        )}
        <div
          style={{
            width: '100%',
            maxWidth: isMobile ? '180px' : '280px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <TeamPicker value={team} onChange={setTeam} />
        </div>
      </div>

      {/* Right — Profile pill */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setDropdownOpen((prev) => !prev)}
          aria-haspopup="true"
          aria-expanded={dropdownOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 12px 4px 4px',
            borderRadius: '9999px',
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            color: 'var(--text-primary, #dfe2eb)',
            cursor: 'pointer',
            fontFamily: 'var(--font-family-body, "Open Sans", sans-serif)',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '9999px',
              background: 'rgba(254, 214, 3, 0.12)',
              color: '#FED603',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}
          >
            {user.initials}
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
            <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary, #dfe2eb)' }}>
              {user.name}
            </span>
            <span style={{ fontSize: '0.6875rem', color: '#888' }}>{user.role}</span>
          </span>
          <ChevronDown
            size={14}
            style={{
              color: 'var(--text-tertiary, #9ea6b4)',
              transform: dropdownOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms ease',
            }}
          />
        </button>

        {/* Placeholder dropdown — sign-out wiring intentionally deferred. */}
        {dropdownOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              minWidth: '200px',
              padding: '12px 14px',
              borderRadius: '8px',
              background: 'rgba(16, 20, 26, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              boxShadow: '0px 24px 48px rgba(0, 0, 0, 0.4)',
              fontSize: '0.75rem',
              color: 'var(--text-tertiary, #9ea6b4)',
              zIndex: 'var(--z-dropdown, 10)',
            }}
          >
            Profile menu (placeholder)
          </div>
        )}
      </div>
    </header>
  );
}

/* -----------------------------------------------------------------------------
 * GmLayout
 * --------------------------------------------------------------------------- */
export default function GmLayout() {
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isMobile);

  // On mobile, sidebar opens as overlay; on desktop it's always visible.
  const sidebarOpen = isMobile ? !sidebarCollapsed : true;

  const handleNavItemClick = useCallback(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);

  const desktopSidebarWidth = sidebarCollapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_OPEN_WIDTH;

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--bg-base, #10141a)',
        color: 'var(--text-primary, #dfe2eb)',
        fontFamily: 'var(--font-family-body, "Open Sans", sans-serif)',
      }}
    >
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarCollapsed(true)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 49,
          }}
        />
      )}

      {/* Sidebar */}
      {(!isMobile || sidebarOpen) && (
        <GmSidebar
          collapsed={isMobile ? false : sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          isMobile={isMobile}
          onNavItemClick={handleNavItemClick}
        />
      )}

      {/* Main column: top bar (in-flow) + scrolling content. No fixed positioning;
          the header takes its 56px naturally, so no padding compensation is needed
          and there is no risk of overlap regardless of viewport / scroll state. */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <GmTopNavbar
          sidebarCollapsed={sidebarCollapsed}
          isMobile={isMobile}
          onToggleSidebar={() => setSidebarCollapsed(false)}
        />
        <main
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--bg-base, #10141a)',
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
